/**
 * React Hook: Get Component Template (AI Prompt) for Create Metadata
 *
 * Calls the RAG prompt-professor API to get an AI prompt template for creating
 * a Salesforce component (ApexClass, LWC, Aura, etc.). The returned template
 * can be used to generate code via AI.
 */

import { useMutation } from '@tanstack/react-query';
import { axiosInstance } from '@/dataProvider/axios';
import { env } from '@/config/env';

// ============================================================================
// Constants
// ============================================================================

const RAG_SERVICE_BASE_URL = `${env.API_URL}/proxy/utilities`;

/** Maps metadata type (e.g. ApexClass) to prompt-professor component_type */
const COMPONENT_TYPE_MAP: Record<string, string> = {
  apextrigger: 'trigger',
  lightningcomponentbundle: 'lwc',
  auradefinitionbundle: 'aura',
  flow: 'flow',
  apexpage: 'visualforce',
};

function mapToComponentType(metadataType: string): string {
  const lower = metadataType.toLowerCase();
  return (
    COMPONENT_TYPE_MAP[lower] ??
    Object.entries(COMPONENT_TYPE_MAP).find(([k]) => lower.includes(k))?.[1] ??
    'apex_class'
  );
}

// ============================================================================
// Types
// ============================================================================

export interface ComponentTemplateInput {
  metadataType: string;
  name: string;
  description: string;
  relatedObjects?: string[];
}

export interface ComponentTemplateResponse {
  ai_prompt_template: string;
}

/** Raw API response (may wrap data in .data) */
interface ComponentTemplateApiResponse {
  ai_prompt_template?: string;
  data?: {
    ai_prompt_template?: string;
  };
}

// ============================================================================
// API
// ============================================================================

const COMPONENT_TEMPLATE_URL = `${RAG_SERVICE_BASE_URL}/prompt-professor/api/salesforce/component-template`;
const REQUEST_TIMEOUT_MS = 30000;

async function fetchComponentTemplate(
  input: ComponentTemplateInput
): Promise<string> {
  const { metadataType, name, description, relatedObjects } = input;
  const componentType = mapToComponentType(metadataType);

  const response = await axiosInstance.post<ComponentTemplateApiResponse>(
    COMPONENT_TEMPLATE_URL,
    {
      component_type: componentType,
      name: name.trim(),
      description: description.trim(),
      related_objects: relatedObjects ?? [],
    },
    { timeout: REQUEST_TIMEOUT_MS }
  );

  const data = response.data;
  const aiPrompt =
    data?.ai_prompt_template ?? data?.data?.ai_prompt_template;

  if (!aiPrompt) {
    throw new Error('No AI prompt template returned');
  }

  return aiPrompt;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to fetch the AI prompt template for creating a Salesforce component.
 * Use the returned template with your AI flow, then create the metadata (e.g. open editor with prompt).
 *
 * @example
 * ```tsx
 * const { getComponentTemplate, isPending, error } = useComponentTemplate();
 *
 * const aiPrompt = await getComponentTemplate({
 *   metadataType: 'ApexClass',
 *   name: 'AccountHandler',
 *   description: 'Handles account business logic',
 * });
 * onSubmit(metadataType, name, aiPrompt);
 * ```
 */
export function useComponentTemplate() {
  const mutation = useMutation({
    mutationFn: fetchComponentTemplate,
    onSuccess: () => {
      console.log('[ComponentTemplate] fetched');
    },
    onError: (error) => {
      console.error('❌ Failed to fetch component template:', error);
    },
  });

  return {
    /** Fetch AI prompt template for the given metadata type, name, and description */
    getComponentTemplate: mutation.mutateAsync,
    /** Whether the request is in progress */
    isPending: mutation.isPending,
    /** Error from the last failed request */
    error: mutation.error,
    /** Reset mutation state */
    reset: mutation.reset,
  };
}
